import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";

import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { apiRequest } from '../../fetchSetup';


import {
  Add,
  Delete,
  Campaign,
  Save,
} from "@mui/icons-material";

import bg from "../Logos/med_icon3.png";

// ─────────────────────────────────────────────
// THEME
// ─────────────────────────────────────────────
const hubTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#006D77",
      light: "#00949f",
      dark: "#004d55",
      contrastText: "#fff",
    },
    secondary: {
      main: "#E29578",
    },
    background: {
      default: "#F1F5F9",
      paper: "#ffffff",
    },
    text: {
      primary: "#0f172a",
      secondary: "#64748b",
    },
  },
  typography: {
    fontFamily: "'DM Sans', 'Inter', sans-serif",
    h5: {
      fontFamily: "'Sora', sans-serif",
      fontWeight: 800,
    },
    h6: {
      fontFamily: "'Sora', sans-serif",
      fontWeight: 700,
    },
  },
  shape: {
    borderRadius: 12,
  },
});

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const TIME_SLOTS = [
  { label: "8 AM", value: 8 },
  { label: "9 AM", value: 9 },
  { label: "10 AM", value: 10 },
  { label: "11 AM", value: 11 },
  { label: "12 PM", value: 12 },
  { label: "1 PM", value: 13 },
  { label: "2 PM", value: 14 },
  { label: "3 PM", value: 15 },
  { label: "4 PM", value: 16 },
  { label: "5 PM", value: 17 },
  { label: "6 PM", value: 18 },
  { label: "7 PM", value: 19 },
  { label: "8 PM", value: 20 },
  { label: "9 PM", value: 21 },
  { label: "10 PM", value: 22 },
  { label: "11 PM", value: 23 },
];
const SCREENS=[
"Book Appointment",
"Medicine Orders",
"Nursing and physio"
]

const DRAWER_WIDTH = 280;
const MOBILE_BAR_H = 56;

const BACKEND_URL = process.env.REACT_APP_PUSH_NOTIFICATIONS;

export default function WeeklyNotificationManagement() {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(
    muiTheme.breakpoints.down("sm")
  );

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    body: "",
    day: "monday",
    hour: 8,
    screen: "Book Appointment",
    is_active: true,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ─────────────────────────────────────────────
  // FETCH
  // ─────────────────────────────────────────────
  const fetchCampaigns = async () => {
    try {
      setLoading(true);

      const response = await apiRequest(
        `${BACKEND_URL}/get-weekly-notifications`
      );

      const data = await response.json();

      setCampaigns(data || []);
    } catch (error) {
      console.error(error);

      setSnackbar({
        open: true,
        message: "Failed to apiRequest campaigns",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // CREATE
  // ─────────────────────────────────────────────
  const createCampaign = async () => {
    try {
      const response = await apiRequest(
        `${BACKEND_URL}/weekly-push-notifications`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error);
      }

      setSnackbar({
        open: true,
        message: "Campaign created successfully",
        severity: "success",
      });

      setFormData({
        title: "",
        body: "",
        day: "monday",
        hour: 8,
        screen: "Book Appointment",
        is_active: true,
      });

      fetchCampaigns();
    } catch (error) {
      console.error(error);

      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  // ─────────────────────────────────────────────
  // DELETE
  // ─────────────────────────────────────────────
  const deleteCampaign = async (campaignId) => {
    try {
      await apiRequest(
        `${BACKEND_URL}/weekly-notifications/${campaignId}`,
        {
          method: "DELETE",
        }
      );

      fetchCampaigns();

      setSnackbar({
        open: true,
        message: "Campaign deleted",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
    }
  };

  // ─────────────────────────────────────────────
  // TOGGLE ACTIVE
  // ─────────────────────────────────────────────
  const toggleActive = async (campaign) => {
    try {
      await apiRequest(
        `${BACKEND_URL}/weekly-notifications/${campaign.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            is_active: !campaign.is_active,
          }),
        }
      );

      fetchCampaigns();
    } catch (error) {
      console.error(error);
    }
  };

  // ─────────────────────────────────────────────
  // INITIAL LOAD
  // ─────────────────────────────────────────────
  useEffect(() => {
    fetchCampaigns();
  }, []);

  return (
    <ThemeProvider theme={hubTheme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#F1F5F9",
          ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
          pt: { xs: `${MOBILE_BAR_H}px`, md: 0 },
          width: {
            xs: "100%",
            md: `calc(100% - ${DRAWER_WIDTH}px)`,
          },
          overflowX: "hidden",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            backgroundImage: `
              linear-gradient(135deg, rgba(0,109,119,0.9) 0%, rgba(0,169,181,0.68) 100%),
              url(${bg})
            `,
            backgroundSize: "cover",
            backgroundPosition: "center",
            px: { xs: 2, sm: 4 },
            pt: { xs: 2.5, sm: 3 },
            pb: 3,
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontWeight: 900,
              fontSize: {
                xs: 18,
                sm: 22,
                md: 26,
              },
              color: "#fff",
            }}
          >
            🔔 Weekly Notification Set Up
          </Typography>

          <Typography
            sx={{
              fontSize: {
                xs: 11,
                sm: 13,
              },
              color: "rgba(255,255,255,0.7)",
              mt: 0.5,
            }}
          >
            Manage weekly push notifications
          </Typography>
        </Box>

        {/* CONTENT */}
        <Box
          sx={{
            p: { xs: 2, md: 4 },
          }}
        >
          {/* CREATE FORM */}
          <Card
            sx={{
              borderRadius: 4,
              boxShadow:
                "0 4px 20px rgba(0,0,0,0.06)",
              mb: 4,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: 18,
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Add /> Create Notification
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notification Title"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        title: e.target.value,
                      })
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notification Body"
                    value={formData.body}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        body: e.target.value,
                      })
                    }
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Day"
                    value={formData.day}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        day: e.target.value,
                      })
                    }
                  >
                    {DAYS.map((day) => (
                      <MenuItem
                        key={day}
                        value={day}
                      >
                        {day.toUpperCase()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

               <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Time Slot"
                  value={formData.hour}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      hour: Number(e.target.value),
                    })
                  }
                >
                  {TIME_SLOTS.map((slot) => (
                    <MenuItem
                      key={slot.value}
                      value={slot.value}
                    >
                      {slot.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Screen"
                    value={formData.screen}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        screen: e.target.value,
                      })
                    }
                  >
                    {SCREENS.map((day) => (
                      <MenuItem
                        key={day}
                        value={day}
                      >
                        {day.toUpperCase()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          formData.is_active
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_active:
                              e.target.checked,
                          })
                        }
                      />
                    }
                    label="Active Notification"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={createCampaign}
                    sx={{
                      background:
                        "#006D77",
                      borderRadius: 3,
                      px: 4,
                      py: 1.2,
                      fontWeight: 700,
                    }}
                  >
                    Save Notification
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* LIST */}
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 20,
              mb: 3,
              color: "#0f172a",
            }}
          >
            📅 Scheduled Notifications
          </Typography>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 8,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {campaigns.map((campaign) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={4}
                  key={campaign.id}
                >
                  <Card
                    sx={{
                      borderRadius: 4,
                      height: "100%",
                      boxShadow:
                        "0 4px 18px rgba(0,0,0,0.06)",
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent:
                            "space-between",
                          alignItems:
                            "flex-start",
                          mb: 2,
                        }}
                      >
                        <Campaign
                          sx={{
                            color:
                              "#006D77",
                            fontSize: 34,
                          }}
                        />

                        <Chip
                          label={
                            campaign.is_active
                              ? "ACTIVE"
                              : "INACTIVE"
                          }
                          color={
                            campaign.is_active
                              ? "success"
                              : "default"
                          }
                        />
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: 18,
                          mb: 1,
                        }}
                      >
                        {campaign.title}
                      </Typography>

                      <Typography
                        sx={{
                          color: "#64748b",
                          fontSize: 14,
                          mb: 2,
                        }}
                      >
                        {campaign.body}
                      </Typography>

                      <Divider
                        sx={{ my: 2 }}
                      />

                      <Typography
                        sx={{ mb: 1 }}
                      >
                        📅{" "}
                        <b>
                          {campaign.day.toUpperCase()}
                        </b>
                      </Typography>

                      <Typography
                        sx={{ mb: 2 }}
                      >
                        ⏰{" "}
                        <b>
                          {campaign.hour}
                        </b>
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 2 }}
                      >
                        * Use 24-hour format (e.g. 20 = 8 PM)
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent:
                            "space-between",
                          alignItems:
                            "center",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                campaign.is_active
                              }
                              onChange={() =>
                                toggleActive(
                                  campaign
                                )
                              }
                            />
                          }
                          label="Active"
                        />

                        <IconButton
                          color="error"
                          onClick={() =>
                            deleteCampaign(
                              campaign.id
                            )
                          }
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* SNACKBAR */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() =>
            setSnackbar({
              ...snackbar,
              open: false,
            })
          }
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}